import React, { useEffect } from "react";
import { useRaceTimeRemaining } from "@/contexts/RaceCountdownContext";
import useCountdown from "@/hooks/useCountdown";
import { formatTimeOfDay } from "@/utils/time-format";

/**
 * Component that displays the time until a race starts
 *
 * @param {Object} props - Component props
 * @param {string} props.marketStartTime - ISO string of market start time
 * @param {string} props.raceId - ID of the race (if using context)
 * @param {string} props.variant - Display variant ('default', 'compact', 'jumps')
 * @param {boolean} props.showSeconds - Whether to show seconds in the countdown
 */
export default function RaceCountdown({
  marketStartTime,
  raceId,
  variant = "default",
  showSeconds = false,
}) {
  // Always call both hooks to maintain hook order consistency
  // This prevents the React hooks rules violation
  const contextCountdown = useRaceTimeRemaining(raceId, {
    includeSeconds: true,
  });
  const directCountdown = useCountdown(marketStartTime, {
    includeSeconds: true,
  });

  // Use the appropriate countdown based on which props were provided
  const countdown = raceId ? contextCountdown : directCountdown;

  if (!countdown) return "";

  const { isComplete, formattedTime, totalSeconds, minutes, seconds } =
    countdown;

  // For completed races
  if (isComplete) {
    // "Resulted @ 1:10 pm": site-wide format, Sydney time
    const localTime = formatTimeOfDay(marketStartTime);

    // Match the styling pattern from market-start-time.jsx
    return variant === "jumps" ? (
      <span className="type-A flex items-center justify-end w-full h-full right-0 transition-all duration-300 transform">
        Resulted @ <span className="normal-case">{localTime}</span>
      </span>
    ) : (
      ""
    );
  }

  // Always show seconds when under 60 seconds, regardless of showSeconds prop
  if (totalSeconds < 60) {
    return (
      <span className="race-countdown-seconds text-red-500 font-bold animate-pulse">
        {seconds}s
      </span>
    );
  }

  // Different display formats based on variant
  switch (variant) {
    case "compact":
      return <span className="race-countdown-compact">({formattedTime})</span>;

    case "jumps":
      return (
        <span className="race-countdown-jumps">
          Jumps in {minutes} min{minutes !== 1 ? "s" : ""}
        </span>
      );

    case "default":
    default:
      return <span className="race-countdown">{formattedTime}</span>;
  }
}
